// --- Dependencies -------------------------------------------------------------------------------
import { NavLink, useLocation } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useNavTabs from "@/hooks/useNavTabs";
import useTranslation from "@/hooks/useTranslation";
import { isSectionReentry } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The desktop navigation, on the inline-start edge — the right in Arabic, the left in English, with
 * neither written down. The icon comes first in the markup for the same reason: it belongs at the
 * start of the row whichever way the row runs.
 *
 * Painted in the chrome colour rather than white, which is the one way this app looks different from
 * `clients-web` — an admin who has both open should never have to read the tabs to know which is
 * which.
 */
export default function SideBar() {
    const t = useTranslation();
    const { tabs, footerTabs } = useNavTabs();
    const { pathname } = useLocation();

    return (
        <aside className="side-bar">
            {/* Where `clients-web` puts its organization switcher. An admin is above every tenant and
                has nothing to switch between, so the slot carries the product instead of going empty. */}
            <div className="side-bar__brand">
                <img className="side-bar__logo" src={constants.logo.round} alt="" />
                <div className="side-bar__brand-text">
                    <span className="side-bar__brand-name">{t.sideBar.brand}</span>
                    <span className="side-bar__brand-kind">{t.sideBar.kind}</span>
                </div>
            </div>

            <span className="side-bar__divider" />

            <nav className="side-bar__nav">
                {tabs.map((tab) => (
                    <NavLink
                        key={tab.path}
                        to={tab.path}
                        end={tab.path === "/"}
                        replace={isSectionReentry(tab.path, pathname)}
                        className={({ isActive }) => `side-bar__tab${isActive ? " side-bar__tab--active" : ""}`}
                    >
                        <Icon name={tab.icon} size={20} />
                        <span className="side-bar__tab-label">{tab.label}</span>
                        {/* The nav itself says which sections are still scaffolding. */}
                        {tab.isPending && <span className="side-bar__pending" title={t.pending.navTitle} />}
                    </NavLink>
                ))}
            </nav>

            {/* Pushed to the foot by the nav above it, which takes the free space. Managing Lozicore's
                own team is housekeeping, not the work. */}
            {footerTabs.length > 0 && (
                <nav className="side-bar__footer">
                    <span className="side-bar__divider" />
                    {footerTabs.map((tab) => (
                        <NavLink
                            key={tab.path}
                            to={tab.path}
                            replace={isSectionReentry(tab.path, pathname)}
                            className={({ isActive }) => `side-bar__tab${isActive ? " side-bar__tab--active" : ""}`}
                        >
                            <Icon name={tab.icon} size={20} />
                            <span className="side-bar__tab-label">{tab.label}</span>
                            {tab.isPending && <span className="side-bar__pending" title={t.pending.navTitle} />}
                        </NavLink>
                    ))}
                </nav>
            )}
        </aside>
    );
}
